import type { TournamentPrizesCategorie, TournamentPrizesSchema } from "@teamz/common";

import AddIcon from "@/assets/add.svg";
import EditIcon from "@/assets/edit.svg";
import DotsIcon from "@/assets/placeholder-dots.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tournamentPrizesSchema } from "@teamz/common";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle } from "../ui/sheet";
import { TextField } from "../ui/text-field";

const PrizeMoneyForm = ({ tournament, prizeCategorie, prize, closeModal }: { tournament: RouterOutputs["tournament"]["getTournament"]; prizeCategorie: TournamentPrizesCategorie; prize: number | null; closeModal: () => void }) => {
  const { t } = useTranslation(["common"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.tournament.updatePrize.mutationOptions({
    onSuccess: () => {
      closeModal();
      queryClient.invalidateQueries(trpc.tournament.getTournament.queryFilter());
    },
  }));

  const { mutate: mutateDelete, isPending: isDeleting } = useMutation(trpc.tournament.deletePrize.mutationOptions({
    onSuccess: () => {
      closeModal();
      queryClient.invalidateQueries(trpc.tournament.getTournament.queryFilter());
    },
  }));

  const prizeForm = useForm<TournamentPrizesSchema>({
    resolver: zodResolver(tournamentPrizesSchema),
    defaultValues: {
      prizeCategorieToUpdate: prizeCategorie,
      prize,
    },
  });

  const onSubmit = prizeForm.handleSubmit((data) => {
    mutate({
      tournamentId: tournament.id,
      tournamentPrizesSchema: data,
    });
  });

  const onDelete = () => {
    mutateDelete({
      tournamentId: tournament.id,
      prizeToDelete: prizeCategorie,
    });
  };

  return (
    <>
      <SheetForm id="PrizeForm" onSubmit={onSubmit}>
        <TextField
          type="number"
          min={0}
          {...prizeForm.register("prize")}
          errorMessage={
            // @ts-expect-error Error type on null value
            prizeForm.formState.errors.prize?.message ? t(prizeForm.formState.errors.prize.message) : undefined
          }
        />
      </SheetForm>

      {prize && (
        <Button
          type="button"
          variant="ghost"
          className="text-white"
          onClick={onDelete}
        >
          {t("common:delete")}
        </Button>
      )}

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PrizeForm" loading={isPending || isDeleting || prizeForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
};

const PrizeButton = ({ onClick, categorieNumber, categoriePrize, tournamentPrize, className }: { onClick: () => void; categorieNumber: string; categoriePrize: string; tournamentPrize: number | null; className: string }) => {
  const { user } = useMe();
  const { t } = useTranslation(["tournaments"]);

  return (
    <button
      type="button"
      className={cn(
        "flex h-14 w-full items-center gap-2 rounded-[10px] p-1",
        className,
        user.role === "player" && "pointer-events-none",

      )}
      onClick={onClick}
    >
      <div className="flex flex-1 items-center gap-1.5 pl-3">
        <Badge variant="orange-dark" size="compact">
          {categorieNumber}
        </Badge>
        <span className="font-semibold">
          {tDyn(t, `tournaments:prize.${categoriePrize}`)}
        </span>
      </div>

      <div className={cn(
        "relative flex h-full flex-1 items-center justify-center rounded-lg bg-white",
        tournamentPrize === null && user.role === "club" && "border border-dashed",
      )}
      >
        <span className="font-semibold">
          {tournamentPrize
            ? `${tournamentPrize.toLocaleString()} €`
            : (
                user.role === "club"
                  ? (
                      <AddIcon className="text-orange-500" />
                    )
                  : (
                      <DotsIcon className="text-neutral-500" />
                    )
              )}
        </span>

        {tournamentPrize && user.role === "club" && <EditIcon className="absolute right-2 top-1/2 size-5 -translate-y-1/2 text-neutral-500" />}
      </div>
    </button>
  );
};

export function TournamentPrizes({ tournament }: { tournament: RouterOutputs["tournament"]["getTournament"] }) {
  const [modalState, updateModalState] = useState<"open" | "closed">("closed");
  const [prizeCategorie, updatePrizeCategorie] = useState<TournamentPrizesCategorie>();
  const isModalOpen = modalState === "open";
  const { user } = useMe();
  const { t } = useTranslation(["tournaments"]);

  return (
    <>
      <div className="flex flex-col gap-2">
        <h3 className="p-2 font-semibold leading-[1.1] text-neutral-500">Prize Money</h3>
        <div className="flex flex-col gap-2 rounded-[20px] bg-white p-2">
          <PrizeButton
            onClick={() => {
              updateModalState("open");
              updatePrizeCategorie("winnerPrize");
            }}
            categorieNumber="1"
            categoriePrize="winnerPrize"
            tournamentPrize={tournament.winnerPrize}
            className="bg-orange-100"
          />
          <PrizeButton
            onClick={() => {
              updateModalState("open");
              updatePrizeCategorie("finalistPrize");
            }}
            categorieNumber="2"
            categoriePrize="finalistPrize"
            tournamentPrize={tournament.finalistPrize}
            className="bg-neutral-100"
          />
          <PrizeButton
            onClick={() => {
              updateModalState("open");
              updatePrizeCategorie("semiFinalistPrize");
            }}
            categorieNumber="3"
            categoriePrize="semiFinalistPrize"
            tournamentPrize={tournament.semiFinalistPrize}
            className="bg-neutral-100"
          />
          <PrizeButton
            onClick={() => {
              updateModalState("open");
              updatePrizeCategorie("quarterFinalistPrize");
            }}
            categorieNumber="4"
            categoriePrize="quarterFinalistPrize"
            tournamentPrize={tournament.quarterFinalistPrize}
            className="bg-neutral-100"
          />
          <PrizeButton
            onClick={() => {
              updateModalState("open");
              updatePrizeCategorie("roundOf16Prize");
            }}
            categorieNumber="5"
            categoriePrize="roundOf16Prize"
            tournamentPrize={tournament.roundOf16Prize}
            className="bg-neutral-100"
          />
        </div>
      </div>

      {user.role === "club" && (
        <Sheet
          open={isModalOpen}
          onOpenChange={() => {
            updateModalState("closed");
          }}
        >
          <SheetContent>
            <SheetHeader>
              <SheetTitle>
                {tDyn(t, `tournaments:prize.${prizeCategorie}`)}
              </SheetTitle>
            </SheetHeader>

            {prizeCategorie && (
              <PrizeMoneyForm
                tournament={tournament}
                prizeCategorie={prizeCategorie}
                prize={tournament[prizeCategorie]}
                closeModal={() => updateModalState("closed")}
              />
            )}

          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
